import React, { useState } from 'react';
import { useToast } from "@chakra-ui/react";
import LoginGraphics from "../../images/loginv2.png";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { onHandelLogin, onForgotPassword, onVerifyOtp, onResetPassword } from '../../http/login';
import { Credencials } from "../../interfaes/user";
import Joi from 'joi';
import { errorFormat, FormattedError, getErrorMessage, hasErrorMessage } from '../../utils/formUtils';

/* ─────────────────────────────────────────────
   Design System
   Aesthetic: Warm Civic Minimal
   Fonts: Plus Jakarta Sans (display) + Inter (body)
   Primary: Royal Blue #1b73e8 → #1558c0
   Background: #f0f4ff soft blue-white gradient
   Card: pure white
   Text: #111827 charcoal
   ───────────────────────────────────────────── */

const styles: Record<string, React.CSSProperties> = {
    /* Page */
    page: {
        height: '100vh',
        background: 'linear-gradient(135deg, #eef2ff 0%, #f0f7ff 50%, #e8f4fd 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
        position: 'relative',
        overflow: 'hidden',
    },
    /* Ambient background shapes */
    bgShape1: {
        position: 'fixed',
        top: '-15%',
        left: '-10%',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(27,115,232,0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    bgShape2: {
        position: 'fixed',
        bottom: '-20%',
        right: '-10%',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 70%)',
        pointerEvents: 'none',
    },

    /* Main card — fixed to viewport, no scroll */
    card: {
        display: 'flex',
        width: '100%',
        maxWidth: '980px',
        height: 'min(92vh, 620px)',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(15, 30, 80, 0.13), 0 4px 14px rgba(15, 30, 80, 0.06)',
        background: '#ffffff',
        position: 'relative',
        zIndex: 1,
    },

    /* ─── LEFT PANEL ─── */
    leftPanel: {
        flex: '0 0 58%',
        background: 'linear-gradient(160deg, #f8faff 0%, #eef4ff 100%)',
        padding: '28px 36px 20px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
    },

    /* Decorative dots grid */
    dotsGrid: {
        position: 'absolute',
        top: '32px',
        left: '32px',
        opacity: 0.07,
        backgroundImage: 'radial-gradient(circle, #1b73e8 1.5px, transparent 1.5px)',
        backgroundSize: '20px 20px',
        width: '160px',
        height: '120px',
        borderRadius: '8px',
    },
    dotsGridRight: {
        position: 'absolute',
        bottom: '80px',
        right: '28px',
        opacity: 0.06,
        backgroundImage: 'radial-gradient(circle, #6366f1 1.5px, transparent 1.5px)',
        backgroundSize: '18px 18px',
        width: '120px',
        height: '90px',
        borderRadius: '8px',
    },

    /* Decorative blobs behind illustration */
    blob1: {
        position: 'absolute',
        top: '120px',
        left: '-40px',
        width: '340px',
        height: '280px',
        borderRadius: '60% 40% 55% 45% / 45% 55% 45% 55%',
        background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(27,115,232,0.06) 100%)',
        pointerEvents: 'none',
    },
    blob2: {
        position: 'absolute',
        top: '160px',
        right: '20px',
        width: '200px',
        height: '200px',
        borderRadius: '50% 60% 40% 55% / 55% 40% 60% 45%',
        background: 'linear-gradient(135deg, rgba(34,197,94,0.07) 0%, rgba(27,115,232,0.04) 100%)',
        pointerEvents: 'none',
    },

    /* Small geometric accents */
    accent: {
        position: 'absolute',
        pointerEvents: 'none',
    },

    /* Header */
    leftHeader: {
        position: 'relative',
        zIndex: 2,
    },
    appTitle: {
        fontSize: '18px',
        fontWeight: 800,
        color: '#0f172a',
        lineHeight: 1.25,
        letterSpacing: '-0.4px',
        marginBottom: '4px',
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
    },
    appSubtitle: {
        fontSize: '12.5px',
        color: '#1e3a5f',
        fontWeight: 500,
        lineHeight: 1.5,
        marginBottom: '0',
        fontFamily: "'Inter', sans-serif",
    },

    /* Illustration composition */
    illustrationWrap: {
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        marginTop: '8px',
        marginBottom: '6px',
        height: '205px',
    },
    illustrationCircle: {
        position: 'absolute',
        bottom: '0',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '310px',
        height: '155px',
        borderRadius: '50% 50% 0 0',
        background: 'linear-gradient(180deg, rgba(27,115,232,0.1) 0%, rgba(99,102,241,0.07) 100%)',
    },
    illustrationImg: {
        height: '200px',
        width: 'auto',
        maxWidth: '100%',
        position: 'relative',
        zIndex: 2,
        filter: 'drop-shadow(0 6px 18px rgba(27,115,232,0.15))',
    },

    /* Feature badges */
    featuresRow: {
        display: 'flex',
        gap: '10px',
        marginTop: '22px',
        position: 'relative',
        zIndex: 2,
    },
    featureCard: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '10px 8px',
        borderRadius: '12px',
        background: '#ffffff',
        boxShadow: '0 2px 10px rgba(15, 30, 80, 0.07)',
        border: '1px solid rgba(27,115,232,0.08)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    },
    featureIconWrap: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '6px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.07)',
    },
    featureLabel: {
        fontSize: '11.5px',
        fontWeight: 700,
        color: '#0f172a',
        marginBottom: '2px',
    },
    featureDesc: {
        fontSize: '10.5px',
        color: '#374151',
        lineHeight: 1.35,
        fontWeight: 500,
    },

    /* Footer */
    leftFooter: {
        marginTop: 'auto',
        paddingTop: '10px',
        textAlign: 'center',
        position: 'relative',
        zIndex: 2,
    },
    footerText: {
        fontSize: '10.5px',
        color: '#4b5563',
        lineHeight: 1.55,
    },
    footerLink: {
        color: '#1452a8',
        textDecoration: 'none',
        fontWeight: 700,
    },

    /* ─── RIGHT PANEL ─── */
    rightPanel: {
        flex: '0 0 42%',
        background: 'linear-gradient(160deg, #1b73e8 0%, #1252c0 100%)',
        padding: '28px 36px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
    },

    /* Decorative circles on right panel */
    rightCircle1: {
        position: 'absolute',
        top: '-80px',
        right: '-80px',
        width: '280px',
        height: '280px',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.04)',
        pointerEvents: 'none',
    },
    rightCircle2: {
        position: 'absolute',
        bottom: '-100px',
        left: '-60px',
        width: '320px',
        height: '320px',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.03)',
        pointerEvents: 'none',
    },
    rightCircle3: {
        position: 'absolute',
        top: '50%',
        right: '-120px',
        transform: 'translateY(-50%)',
        width: '240px',
        height: '240px',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.025)',
        pointerEvents: 'none',
    },

    /* Welcome section */
    welcomeSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '20px',
        position: 'relative',
        zIndex: 2,
    },
    iconSquare: {
        width: '46px',
        height: '46px',
        borderRadius: '12px',
        background: 'rgba(255,255,255,0.15)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '12px',
        border: '1px solid rgba(255,255,255,0.2)',
    },
    welcomeTitle: {
        fontSize: '20px',
        fontWeight: 800,
        color: '#ffffff',
        textAlign: 'center',
        lineHeight: 1.25,
        letterSpacing: '-0.3px',
        marginBottom: '6px',
    },
    welcomeSubtitle: {
        fontSize: '13px',
        color: 'rgba(255,255,255,0.92)',
        textAlign: 'center',
        fontWeight: 500,
    },

    /* Form */
    form: {
        position: 'relative',
        zIndex: 2,
    },
    fieldGroup: {
        marginBottom: '12px',
    },
    fieldLabel: {
        display: 'block',
        fontSize: '12.5px',
        fontWeight: 600,
        color: '#ffffff',
        marginBottom: '6px',
        letterSpacing: '0.02em',
    },
    inputWrap: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },
    inputIcon: {
        position: 'absolute',
        left: '14px',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        pointerEvents: 'none',
        zIndex: 1,
    },
    input: {
        width: '100%',
        height: '48px',
        borderRadius: '10px',
        border: '1.5px solid rgba(255,255,255,0.15)',
        background: 'rgba(255,255,255,0.97)',
        paddingLeft: '42px',
        paddingRight: '16px',
        fontSize: '14px',
        color: '#111827',
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
        fontWeight: 400,
        outline: 'none',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        boxSizing: 'border-box',
    },
    inputWithToggle: {
        paddingRight: '44px',
    },
    eyeBtn: {
        position: 'absolute',
        right: '12px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px',
        color: '#6b7280',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '6px',
        transition: 'color 0.15s ease',
    },
    forgotRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '6px',
        marginBottom: '2px',
    },
    forgotLink: {
        fontSize: '12px',
        color: '#ffffff',
        cursor: 'pointer',
        textDecoration: 'none',
        fontWeight: 600,
        transition: 'color 0.15s ease',
        background: 'none',
        border: 'none',
        padding: '2px 0',
    },
    loginBtn: {
        width: '100%',
        height: '48px',
        marginTop: '14px',
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #1252c0 0%, #0e3d96 100%)',
        border: '1.5px solid rgba(255,255,255,0.25)',
        color: '#ffffff',
        fontSize: '14.5px',
        fontWeight: 700,
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
        cursor: 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
        letterSpacing: '0.01em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
    },
    errorText: {
        fontSize: '11.5px',
        color: '#fecaca',
        marginTop: '4px',
        fontWeight: 600,
    },

    /* Forgot password flow */
    forgotTitle: {
        fontSize: '20px',
        fontWeight: 800,
        color: '#ffffff',
        textAlign: 'center',
        marginBottom: '16px',
        letterSpacing: '-0.3px',
    },
    stepIndicator: {
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        marginBottom: '20px',
    },
    stepDot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        transition: 'background 0.2s ease, transform 0.2s ease',
    },
    backBtn: {
        background: 'none',
        border: 'none',
        color: '#ffffff',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginTop: '12px',
        padding: '4px 0',
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
        transition: 'color 0.15s ease',
    },
    outlineBtn: {
        width: '100%',
        height: '48px',
        marginTop: '12px',
        borderRadius: '10px',
        background: 'rgba(255,255,255,0.12)',
        border: '1.5px solid rgba(255,255,255,0.3)',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 600,
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
        cursor: 'pointer',
        transition: 'background 0.15s ease',
    },
    resendText: {
        fontSize: '12.5px',
        color: '#ffffff',
        textAlign: 'center',
        marginTop: '10px',
    },
};

/* ─── SVG Icons ─── */
const PhoneIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .82h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
    </svg>
);

const LockIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
);

const EyeIcon = ({ open }: { open: boolean }) => open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

const EducationIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
        <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
    </svg>
);

const PersonIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" />
        <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
);

const ClipboardIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="2" />
        <line x1="9" y1="12" x2="15" y2="12" />
        <line x1="9" y1="16" x2="13" y2="16" />
    </svg>
);

const TrendingIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
    </svg>
);

const ChevronLeft = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

const Spinner = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
);

/* ─── Feature data ─── */
const features = [
    {
        icon: <PersonIcon />,
        color: '#6366f1',
        bg: '#eef2ff',
        label: 'Individualized',
        desc: 'Plans tailored for every learner.',
    },
    {
        icon: <ClipboardIcon />,
        color: '#1b73e8',
        bg: '#eff6ff',
        label: 'Organized',
        desc: 'Centralized student information.',
    },
    {
        icon: <TrendingIcon />,
        color: '#16a34a',
        bg: '#f0fdf4',
        label: 'Progress Tracking',
        desc: 'Monitor goals & achievements.',
    },
];

/* ─── Main Component ─── */
export default function LoginPage() {
    const toast = useToast();
    const schema = Joi.object({
        username: Joi.string().min(3).max(50).required(),
        password: Joi.string().required(),
    });

    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const { t } = useTranslation('login');
    const [showPassword, setShowPassword] = useState(false);
    const [loginObj, setLoginObj] = useState<Credencials>({ username: '', password: '' });
    const [formErrors, setFormErrors] = useState<FormattedError[]>([]);

    /* Forgot password flow */
    const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
    const [step, setStep] = useState(1);
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [timer, setTimer] = useState(0);

    /* ─── Hover state helpers ─── */
    const [btnHover, setBtnHover] = useState(false);
    const [inputFocus, setInputFocus] = useState<string | null>(null);

    const validateObj = () => {
        const { error } = schema.validate(loginObj);
        if (error) { setFormErrors(errorFormat(error.details)); return false; }
        setFormErrors([]);
        return true;
    };

    const onLogin = async () => {
        if (validateObj()) {
            try {
                setLoading(true);
                const response = await onHandelLogin(loginObj);
                localStorage.setItem('user', JSON.stringify(response.data));
                setLoading(false);
                toast({ title: 'Welcome!', description: 'Welcome to IEP!', status: 'success', duration: 3000, isClosable: true });
                navigate('/');
            } catch (e) {
                setLoading(false);
                toast({ title: 'Login Failed', description: 'We could not log you in. Please contact support.', status: 'error', duration: 3000, isClosable: true });
            }
        }
    };

    const handleSendOtp = async () => {
        if (!mobile || mobile.length !== 10) {
            toast({ title: 'Error', description: 'Please enter a valid 10-digit mobile number.', status: 'error', duration: 3000 });
            return;
        }
        setLoading(true);
        try {
            await onForgotPassword(mobile);
            setStep(2);
            setTimer(60);
            const interval = setInterval(() => {
                setTimer(prev => { if (prev <= 1) clearInterval(interval); return prev - 1; });
            }, 1000);
            toast({ title: 'OTP Sent', description: 'OTP sent successfully.', status: 'success', duration: 3000 });
        } catch (e: any) {
            const msg = e.response?.data?.message || 'Failed to send OTP';
            toast({ title: 'Error', description: msg, status: 'error', duration: 5000 });
        } finally { setLoading(false); }
    };

    const handleVerifyOtp = async () => {
        setLoading(true);
        try {
            await onVerifyOtp(mobile, otp);
            setStep(3);
            toast({ title: 'OTP Verified', description: 'OTP verified successfully.', status: 'success', duration: 3000 });
        } catch (e: any) {
            const msg = e.response?.data?.message || 'Invalid OTP';
            toast({ title: 'Error', description: msg, status: 'error', duration: 5000 });
        } finally { setLoading(false); }
    };

    const handleResetPassword = async () => {
        if (newPassword !== confirmPassword) {
            toast({ title: 'Error', description: 'Passwords do not match.', status: 'error', duration: 3000 });
            return;
        }
        setLoading(true);
        try {
            await onResetPassword({ mobile, otp, newPassword, confirmPassword });
            toast({ title: 'Password Changed', description: 'Please login with your new password.', status: 'success', duration: 3000 });
            setForgotPasswordMode(false);
            setStep(1);
            setMobile(''); setOtp(''); setNewPassword(''); setConfirmPassword('');
        } catch (e: any) {
            const msg = e.response?.data?.message || 'Failed to reset password';
            toast({ title: 'Error', description: msg, status: 'error', duration: 5000 });
        } finally { setLoading(false); }
    };

    const inputStyle = (name: string, extra?: React.CSSProperties): React.CSSProperties => ({
        ...styles.input,
        ...(extra || {}),
        borderColor: inputFocus === name ? '#1b73e8' : 'rgba(229,231,235,1)',
        boxShadow: inputFocus === name ? '0 0 0 3px rgba(27,115,232,0.12)' : 'none',
    });

    return (
        <>
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=Inter:wght@400;500;600&display=swap');

                * { box-sizing: border-box; }

                /* ── Keyframes ── */

                @keyframes fadeUp {
                    from { opacity: 0; transform: translateY(18px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }

                @keyframes slideInLeft {
                    from { opacity: 0; transform: translateX(-24px); }
                    to   { opacity: 1; transform: translateX(0); }
                }

                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(24px); }
                    to   { opacity: 1; transform: translateX(0); }
                }

                @keyframes scaleIn {
                    from { opacity: 0; transform: scale(0.88); }
                    to   { opacity: 1; transform: scale(1); }
                }

                @keyframes floatY {
                    0%, 100% { transform: translateY(0px); }
                    50%       { transform: translateY(-10px); }
                }

                @keyframes floatYSlow {
                    0%, 100% { transform: translateY(0px) scale(1); }
                    50%       { transform: translateY(-16px) scale(1.02); }
                }

                @keyframes rotateSlow {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                }

                @keyframes pulseBorder {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.25); }
                    50%       { box-shadow: 0 0 0 6px rgba(255,255,255,0); }
                }

                @keyframes blobFloat1 {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    33%       { transform: translate(12px, -14px) scale(1.03); }
                    66%       { transform: translate(-8px, 8px) scale(0.97); }
                }

                @keyframes blobFloat2 {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    40%       { transform: translate(-10px, 12px) scale(1.04); }
                    80%       { transform: translate(14px, -8px) scale(0.96); }
                }

                @keyframes bgDrift1 {
                    0%, 100% { transform: translate(0, 0); }
                    50%       { transform: translate(20px, -20px); }
                }

                @keyframes bgDrift2 {
                    0%, 100% { transform: translate(0, 0); }
                    50%       { transform: translate(-20px, 20px); }
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                /* ── Entrance utilities (staggered via inline delay) ── */
                .ani-fade-up    { animation: fadeUp 0.55s cubic-bezier(0.22,1,0.36,1) both; }
                .ani-fade-in    { animation: fadeIn 0.5s ease both; }
                .ani-slide-left { animation: slideInLeft 0.55s cubic-bezier(0.22,1,0.36,1) both; }
                .ani-slide-right{ animation: slideInRight 0.55s cubic-bezier(0.22,1,0.36,1) both; }
                .ani-scale-in   { animation: scaleIn 0.5s cubic-bezier(0.22,1,0.36,1) both; }

                /* ── Persistent ambient animations ── */
                .ani-blob1 { animation: blobFloat1 9s ease-in-out infinite; }
                .ani-blob2 { animation: blobFloat2 11s ease-in-out infinite; }
                .ani-bg1   { animation: bgDrift1 14s ease-in-out infinite; }
                .ani-bg2   { animation: bgDrift2 18s ease-in-out infinite; }
                .ani-float { animation: floatY 4s ease-in-out infinite; }
                .ani-float-slow { animation: floatYSlow 6s ease-in-out infinite; }
                .ani-rotate-slow { animation: rotateSlow 20s linear infinite; }
                .ani-pulse-border { animation: pulseBorder 2.5s ease-in-out infinite; }
                .spin { animation: spin 0.8s linear infinite; }

                /* ── Hover states ── */
                .login-feature-card {
                    transition: transform 0.22s ease, box-shadow 0.22s ease;
                }
                .login-feature-card:hover {
                    transform: translateY(-3px) scale(1.02);
                    box-shadow: 0 8px 24px rgba(15, 30, 80, 0.12) !important;
                }
                .login-btn-primary {
                    transition: transform 0.15s ease, box-shadow 0.15s ease;
                }
                .login-btn-primary:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 28px rgba(14, 61, 150, 0.45) !important;
                }
                .login-btn-primary:active:not(:disabled) { transform: translateY(0); }
                .login-btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }

                .forgot-link { transition: color 0.15s ease, letter-spacing 0.15s ease; }
                .forgot-link:hover { color: white !important; text-decoration: underline; letter-spacing: 0.02em; }

                .back-btn:hover { color: white !important; }
                .eye-btn:hover { color: #4b5563 !important; transform: scale(1.1); }
                .outline-btn { transition: background 0.15s ease, transform 0.12s ease; }
                .outline-btn:hover { background: rgba(255,255,255,0.2) !important; transform: translateY(-1px); }

                .footer-link { transition: color 0.15s ease; }
                .footer-link:hover { color: #1252c0 !important; text-decoration: underline; }

                /* ── Ensure illustration always visible ── */
                .illustration-wrap img { display: block !important; opacity: 1 !important; }

                @media (max-width: 768px) {
                    .login-left-panel  { display: none !important; }
                    .login-card        { flex-direction: column !important; max-width: 440px !important; min-height: unset !important; }
                    .login-right-panel { flex: 1 !important; padding: 40px 28px !important; border-radius: 28px !important; }
                }

                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: 0.01ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: 0.01ms !important;
                    }
                }
            `}</style>

            <div style={styles.page}>
                {/* Ambient background — slow drift */}
                <div className="ani-bg1" style={styles.bgShape1} aria-hidden="true" />
                <div className="ani-bg2" style={styles.bgShape2} aria-hidden="true" />

                {/* ── CARD ── */}
                <main className="ani-scale-in" style={{ ...styles.card, animationDelay: '0ms' }} role="main">

                    {/* ═══ LEFT PANEL ═══ */}
                    <section
                        className="login-left-panel ani-slide-left"
                        style={{ ...styles.leftPanel, animationDelay: '60ms' }}
                        aria-label="Application information"
                    >
                        {/* Decorative elements — blobs float continuously */}
                        <div style={styles.dotsGrid} aria-hidden="true" />
                        <div style={styles.dotsGridRight} aria-hidden="true" />
                        <div className="ani-blob1" style={styles.blob1} aria-hidden="true" />
                        <div className="ani-blob2" style={styles.blob2} aria-hidden="true" />

                        {/* Small geometric accents — slow rotation */}
                        <svg className="ani-rotate-slow" style={{ ...styles.accent, top: '48px', right: '60px', opacity: 0.08, transformOrigin: 'center' }} width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                            <line x1="0" y1="8" x2="16" y2="8" stroke="#1b73e8" strokeWidth="2" />
                            <line x1="8" y1="0" x2="8" y2="16" stroke="#1b73e8" strokeWidth="2" />
                        </svg>
                        <svg className="ani-float" style={{ ...styles.accent, bottom: '130px', left: '48px', opacity: 0.07, animationDuration: '5s' }} width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                            <circle cx="10" cy="10" r="8" fill="none" stroke="#6366f1" strokeWidth="2" />
                        </svg>
                        <svg className="ani-rotate-slow" style={{ ...styles.accent, top: '52%', right: '28px', opacity: 0.06, transformOrigin: 'center', animationDirection: 'reverse', animationDuration: '28s' }} width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                            <rect x="2" y="2" width="10" height="10" fill="none" stroke="#16a34a" strokeWidth="2" strokeDasharray="3 2" />
                        </svg>

                        {/* Header */}
                        <header className="ani-fade-up" style={{ ...styles.leftHeader, animationDelay: '120ms' }}>
                            <h1 style={styles.appTitle}>Individualized Education Plan (IEP)</h1>
                            <p style={styles.appSubtitle}>Supporting better planning, collaboration and learning outcomes.</p>
                        </header>

                        {/* Illustration — gentle float */}
                        <div className="ani-scale-in" style={{ ...styles.illustrationWrap, animationDelay: '200ms' }} aria-label="Three diverse students smiling together">
                            <div style={styles.illustrationCircle} aria-hidden="true" />
                            <img
                                src={LoginGraphics}
                                alt="Three diverse students — inclusive education illustration"
                                style={{ ...styles.illustrationImg, display: 'block' }}
                                draggable={false}
                                loading="eager"
                            />
                        </div>

                        {/* Feature cards — staggered fade-up */}
                        <div style={styles.featuresRow} role="list" aria-label="System features">
                            {features.map((f, i) => (
                                <div
                                    key={f.label}
                                    className="login-feature-card ani-fade-up"
                                    style={{ ...styles.featureCard, animationDelay: `${300 + i * 90}ms` }}
                                    role="listitem"
                                >
                                    <div
                                        style={{ ...styles.featureIconWrap, background: f.bg }}
                                        aria-hidden="true"
                                    >
                                        <span style={{ color: f.color, display: 'flex' }}>{f.icon}</span>
                                    </div>
                                    <p style={styles.featureLabel}>{f.label}</p>
                                    <p style={styles.featureDesc}>{f.desc}</p>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <footer className="ani-fade-in" style={{ ...styles.leftFooter, animationDelay: '600ms' }}>
                            <p style={styles.footerText}>
                                © {new Date().getFullYear()} IEP Management System – National Federation of the Deaf (NDFN)<br />
                                Software developed and maintained by{' '}
                                <a
                                    href="https://diversepatterns.com.np"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="footer-link"
                                    style={styles.footerLink}
                                >
                                    Diverse Patterns
                                </a>
                            </p>
                        </footer>
                    </section>

                    {/* ═══ RIGHT PANEL ═══ */}
                    <section
                        className="login-right-panel ani-slide-right"
                        style={{ ...styles.rightPanel, animationDelay: '80ms' }}
                        aria-label="Login form"
                    >
                        {/* Decorative circles — ambient float */}
                        <div className="ani-float" style={{ ...styles.rightCircle1, animationDuration: '8s' }} aria-hidden="true" />
                        <div className="ani-float" style={{ ...styles.rightCircle2, animationDuration: '11s', animationDelay: '1.5s' }} aria-hidden="true" />
                        <div className="ani-float" style={{ ...styles.rightCircle3, animationDuration: '7s', animationDelay: '0.8s' }} aria-hidden="true" />

                        {!forgotPasswordMode ? (
                            <>
                                {/* Welcome */}
                                <div className="ani-fade-up" style={{ ...styles.welcomeSection, animationDelay: '180ms' }}>
                                    <div className="ani-pulse-border" style={styles.iconSquare} aria-hidden="true">
                                        <EducationIcon />
                                    </div>
                                    <h2 className="ani-fade-up" style={{ ...styles.welcomeTitle, animationDelay: '240ms' }}>Welcome to IEP<br />Management System</h2>
                                    <p className="ani-fade-up" style={{ ...styles.welcomeSubtitle, animationDelay: '290ms' }}>Please sign in to continue.</p>
                                </div>

                                {/* Login form */}
                                <form
                                    style={styles.form}
                                    onSubmit={(e) => { e.preventDefault(); onLogin(); }}
                                    noValidate
                                    aria-label="Sign in form"
                                >
                                    {/* Phone number */}
                                    <div className="ani-fade-up" style={{ ...styles.fieldGroup, animationDelay: '340ms' }}>
                                        <label htmlFor="login-phone" style={styles.fieldLabel}>
                                            {t('login:userName')}
                                        </label>
                                        <div style={styles.inputWrap}>
                                            <span style={styles.inputIcon} aria-hidden="true">
                                                <PhoneIcon />
                                            </span>
                                            <input
                                                id="login-phone"
                                                type="tel"
                                                autoComplete="tel"
                                                placeholder="Enter phone number"
                                                value={loginObj.username}
                                                onChange={(e) => setLoginObj({ ...loginObj, username: e.target.value })}
                                                onFocus={() => setInputFocus('phone')}
                                                onBlur={() => setInputFocus(null)}
                                                style={inputStyle('phone')}
                                                aria-required="true"
                                                aria-describedby={hasErrorMessage(formErrors, 'username') ? 'phone-error' : undefined}
                                                aria-invalid={hasErrorMessage(formErrors, 'username')}
                                            />
                                        </div>
                                        {hasErrorMessage(formErrors, 'username') && (
                                            <p id="phone-error" style={styles.errorText} role="alert">
                                                {getErrorMessage(formErrors, 'username')}
                                            </p>
                                        )}
                                    </div>

                                    {/* Password */}
                                    <div className="ani-fade-up" style={{ ...styles.fieldGroup, animationDelay: '400ms' }}>
                                        <label htmlFor="login-password" style={styles.fieldLabel}>
                                            {t('login:password')}
                                        </label>
                                        <div style={styles.inputWrap}>
                                            <span style={styles.inputIcon} aria-hidden="true">
                                                <LockIcon />
                                            </span>
                                            <input
                                                id="login-password"
                                                type={showPassword ? 'text' : 'password'}
                                                autoComplete="current-password"
                                                placeholder="Enter password"
                                                value={loginObj.password}
                                                onChange={(e) => setLoginObj({ ...loginObj, password: e.target.value })}
                                                onFocus={() => setInputFocus('password')}
                                                onBlur={() => setInputFocus(null)}
                                                style={inputStyle('password', styles.inputWithToggle)}
                                                aria-required="true"
                                                aria-describedby={hasErrorMessage(formErrors, 'password') ? 'password-error' : undefined}
                                                aria-invalid={hasErrorMessage(formErrors, 'password')}
                                            />
                                            <button
                                                type="button"
                                                className="eye-btn"
                                                style={styles.eyeBtn}
                                                onClick={() => setShowPassword(!showPassword)}
                                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                aria-pressed={showPassword}
                                            >
                                                <EyeIcon open={showPassword} />
                                            </button>
                                        </div>
                                        {hasErrorMessage(formErrors, 'password') && (
                                            <p id="password-error" style={styles.errorText} role="alert">
                                                {getErrorMessage(formErrors, 'password')}
                                            </p>
                                        )}
                                    </div>

                                    {/* Forgot password */}
                                    <div className="ani-fade-up" style={{ ...styles.forgotRow, animationDelay: '450ms' }}>
                                        <button
                                            type="button"
                                            className="forgot-link"
                                            style={styles.forgotLink}
                                            onClick={() => setForgotPasswordMode(true)}
                                            aria-label="Forgot your password? Reset it here"
                                        >
                                            Forgot Password?
                                        </button>
                                    </div>

                                    {/* Login button */}
                                    <button
                                        id="login-submit-btn"
                                        type="submit"
                                        className="login-btn-primary ani-fade-up"
                                        style={{ ...styles.loginBtn, animationDelay: '500ms' }}
                                        disabled={loading}
                                        aria-busy={loading}
                                        aria-label="Sign in to IEP Management System"
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spin" aria-hidden="true"><Spinner /></span>
                                                Signing in…
                                            </>
                                        ) : t('login:loginBtn')}
                                    </button>
                                </form>
                            </>
                        ) : (
                            /* ── Forgot Password Flow ── */
                            <div className="ani-fade-up" style={{ position: 'relative', zIndex: 2, animationDelay: '100ms' }}>
                                <h2 style={styles.forgotTitle}>
                                    {step === 1 && 'Reset Password'}
                                    {step === 2 && 'Verify OTP'}
                                    {step === 3 && 'New Password'}
                                </h2>

                                {/* Step indicator */}
                                <div style={styles.stepIndicator} aria-label={`Step ${step} of 3`} role="status">
                                    {[1, 2, 3].map(s => (
                                        <div
                                            key={s}
                                            style={{
                                                ...styles.stepDot,
                                                background: s <= step ? '#ffffff' : 'rgba(255,255,255,0.3)',
                                                transform: s === step ? 'scale(1.3)' : 'scale(1)',
                                            }}
                                            aria-current={s === step ? 'step' : undefined}
                                        />
                                    ))}
                                </div>

                                {step === 1 && (
                                    <form onSubmit={(e) => { e.preventDefault(); handleSendOtp(); }} aria-label="Enter mobile number for password reset">
                                        <div style={styles.fieldGroup}>
                                            <label htmlFor="reset-mobile" style={styles.fieldLabel}>Mobile Number</label>
                                            <div style={styles.inputWrap}>
                                                <span style={styles.inputIcon} aria-hidden="true"><PhoneIcon /></span>
                                                <input
                                                    id="reset-mobile"
                                                    type="tel"
                                                    placeholder="10-digit mobile number"
                                                    value={mobile}
                                                    onChange={(e) => setMobile(e.target.value)}
                                                    onFocus={() => setInputFocus('mobile')}
                                                    onBlur={() => setInputFocus(null)}
                                                    style={inputStyle('mobile')}
                                                    maxLength={10}
                                                    aria-required="true"
                                                />
                                            </div>
                                        </div>
                                        <button type="submit" className="login-btn-primary" style={styles.loginBtn} disabled={loading} aria-busy={loading}>
                                            {loading ? <><span className="spin" aria-hidden="true"><Spinner /></span> Sending…</> : 'Send OTP'}
                                        </button>
                                    </form>
                                )}

                                {step === 2 && (
                                    <form onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }} aria-label="Enter OTP">
                                        <div style={styles.fieldGroup}>
                                            <label htmlFor="reset-otp" style={styles.fieldLabel}>One-Time Password (OTP)</label>
                                            <div style={styles.inputWrap}>
                                                <span style={styles.inputIcon} aria-hidden="true"><LockIcon /></span>
                                                <input
                                                    id="reset-otp"
                                                    type="text"
                                                    inputMode="numeric"
                                                    placeholder="Enter OTP"
                                                    value={otp}
                                                    onChange={(e) => setOtp(e.target.value)}
                                                    onFocus={() => setInputFocus('otp')}
                                                    onBlur={() => setInputFocus(null)}
                                                    style={inputStyle('otp')}
                                                    aria-required="true"
                                                />
                                            </div>
                                        </div>
                                        <button type="submit" className="login-btn-primary" style={styles.loginBtn} disabled={loading} aria-busy={loading}>
                                            {loading ? <><span className="spin" aria-hidden="true"><Spinner /></span> Verifying…</> : 'Verify OTP'}
                                        </button>
                                        <p style={styles.resendText} aria-live="polite">
                                            {timer > 0 ? (
                                                `Resend OTP in ${timer}s`
                                            ) : (
                                                <button type="button" className="outline-btn" style={{ ...styles.outlineBtn, height: '40px', marginTop: '8px', fontSize: '13px' }} onClick={handleSendOtp}>
                                                    Resend OTP
                                                </button>
                                            )}
                                        </p>
                                    </form>
                                )}

                                {step === 3 && (
                                    <form onSubmit={(e) => { e.preventDefault(); handleResetPassword(); }} aria-label="Set new password">
                                        <div style={styles.fieldGroup}>
                                            <label htmlFor="new-password" style={styles.fieldLabel}>New Password</label>
                                            <div style={styles.inputWrap}>
                                                <span style={styles.inputIcon} aria-hidden="true"><LockIcon /></span>
                                                <input
                                                    id="new-password"
                                                    type="password"
                                                    autoComplete="new-password"
                                                    placeholder="Enter new password"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    onFocus={() => setInputFocus('newpw')}
                                                    onBlur={() => setInputFocus(null)}
                                                    style={inputStyle('newpw')}
                                                    aria-required="true"
                                                />
                                            </div>
                                        </div>
                                        <div style={styles.fieldGroup}>
                                            <label htmlFor="confirm-password" style={styles.fieldLabel}>Confirm Password</label>
                                            <div style={styles.inputWrap}>
                                                <span style={styles.inputIcon} aria-hidden="true"><LockIcon /></span>
                                                <input
                                                    id="confirm-password"
                                                    type="password"
                                                    autoComplete="new-password"
                                                    placeholder="Confirm new password"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    onFocus={() => setInputFocus('confpw')}
                                                    onBlur={() => setInputFocus(null)}
                                                    style={inputStyle('confpw')}
                                                    aria-required="true"
                                                />
                                            </div>
                                        </div>
                                        <button type="submit" className="login-btn-primary" style={styles.loginBtn} disabled={loading} aria-busy={loading}>
                                            {loading ? <><span className="spin" aria-hidden="true"><Spinner /></span> Saving…</> : 'Change Password'}
                                        </button>
                                    </form>
                                )}

                                <button
                                    type="button"
                                    className="back-btn"
                                    style={styles.backBtn}
                                    onClick={() => { setForgotPasswordMode(false); setStep(1); }}
                                    aria-label="Return to login page"
                                >
                                    <ChevronLeft /> Back to Login
                                </button>
                            </div>
                        )}
                    </section>
                </main>
            </div>
        </>
    );
}
